"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { FaStore } from "react-icons/fa";

import type { CategoryWithCounts } from "@/types/categoryWithCounts";
import renderOembed from "@/lib/iframeFunction";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

type FilterOption = {
  label: string;
  value: "all" | "popular" | "trending";
};

interface CategoriesProps {
  categories: CategoryWithCounts[];
  error?: string | null;
  initialFilter?: "all" | "popular" | "trending";
  pagination?: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

const filterOptions: FilterOption[] = [
  { label: "All Categories", value: "all" },
  { label: "Most Popular", value: "popular" },
  { label: "Trending", value: "trending" },
];

export default function Categories({
  categories,
  error = null,
  initialFilter = "all",
  pagination,
}: CategoriesProps) {
  const [activeFilter, setActiveFilter] = useState<"all" | "popular" | "trending">(initialFilter);
  const router = useRouter();
  const searchParams = useSearchParams();
  const categoriesSectionRef = useRef<HTMLDivElement>(null);

  // Update filter when URL param changes
  useEffect(() => {
    const filterParam = searchParams.get("filter") as "all" | "popular" | "trending" | null;
    if (filterParam && ["all", "popular", "trending"].includes(filterParam)) {
      setActiveFilter(filterParam);
    }
  }, [searchParams]);

  // Scroll to just after hero section when page changes via URL
  useEffect(() => {
    const pageParam = searchParams.get("page");
    // Only scroll if page param exists (not on initial load)
    if (pageParam) {
      // Wait for DOM to be ready, then scroll directly to just after hero
      const timer = setTimeout(() => {
        const heroSection = document.getElementById("categories-hero");
        if (heroSection) {
          const scrollPosition = heroSection.offsetTop + heroSection.offsetHeight;
          window.scrollTo({ top: scrollPosition, behavior: "smooth" });
        } else if (categoriesSectionRef.current) {
          // Fallback to categories section if hero not found
          const offset = categoriesSectionRef.current.offsetTop - 20;
          window.scrollTo({ top: offset, behavior: "smooth" });
        }
      }, 150);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  // Handle page change
  const handlePageChange = (newPage: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    const params = new URLSearchParams(searchParams.toString());
    if (newPage === 1) {
      params.delete("page");
    } else {
      params.set("page", newPage.toString());
    }
    
    // Prevent default scroll to top by using scroll: false
    router.push(`/categories?${params.toString()}`, { scroll: false });
    
    // Scroll directly to just after hero section
    setTimeout(() => {
      const heroSection = document.getElementById("categories-hero");
      if (heroSection) {
        const scrollPosition = heroSection.offsetTop + heroSection.offsetHeight;
        window.scrollTo({ top: scrollPosition, behavior: "smooth" });
      } else if (categoriesSectionRef.current) {
        // Fallback to categories section if hero not found
        const offset = categoriesSectionRef.current.offsetTop - 20;
        window.scrollTo({ top: offset, behavior: "smooth" });
      }
    }, 100);
  };

  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 0;

  if (error) {
    return (
      <div className="text-center py-20 text-red-500">
        Error loading categories: {error}
      </div>
    );
  }

  return (
    <div ref={categoriesSectionRef} className="max-w-7xl mx-auto px-4 py-10">
      {/* Header with filters */}
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <h2 className="text-3xl font-bold">All Categories</h2>
        <div className="flex gap-2">
          {filterOptions.map((filter) => (
            <button
              key={filter.value}
              onClick={() => {
                setActiveFilter(filter.value);
                // Update URL without page reload, reset to page 1 when filter changes
                const params = new URLSearchParams(searchParams.toString());
                if (filter.value === "all") {
                  params.delete("filter");
                } else {
                  params.set("filter", filter.value);
                }
                params.delete("page"); // Reset to page 1
                router.push(`/categories?${params.toString()}`, { scroll: false });
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition ${activeFilter === filter.value
                  ? "bg-purple-600 text-white border-purple-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300"
                }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.length === 0 && (
          <p className="text-center text-gray-500 col-span-full">
            No categories found.
          </p>
        )}
        {categories.map((category) => (
          <div
            key={category._id}
            className="border rounded-xl p-8 min-h-[320px] hover:shadow-lg transition-all flex flex-col justify-between h-full"
          >
            <div className="flex flex-col items-center text-center">
              {/* Icon */}
              <FaStore className="text-purple-600 text-4xl mb-4" />

              {/* Title */}
              <h3 className="text-xl font-semibold text-purple-700 mb-3">
                {category.name}
              </h3>

              {/* Description */}
              {category.description && (
                <div
                  className="text-sm text-gray-500 line-clamp-3 prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderOembed(category.description) }}
                />
              )}
            </div>

            {/* Bottom row counts */}
            <div className="mt-6 flex justify-between items-center text-sm font-medium text-gray-700">
              <span>{category.totalStores}+ Stores</span>
              <span>{category.totalCoupons}+ Coupons</span>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center w-full">
              <button
                className="bg-purple-600 text-white px-5 py-2 rounded-md text-sm hover:bg-purple-700 w-full sm:w-auto"
                onClick={() =>
                  router.push(`/coupons?categories=${category.slug}`)
                }
              >
                Browse Deals
              </button>
              <button
                className="bg-gray-100 text-gray-700 px-5 py-2 rounded-md text-sm hover:bg-gray-200 w-full sm:w-auto"
                onClick={() =>
                  router.push(`/stores?category=${category.slug}`)
                }
              >
                View Stores
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="mt-10 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => handlePageChange(Math.max(1, currentPage - 1), e)}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {/* Show limited page numbers */}
              {Array.from(
                { length: Math.min(totalPages, 7) },
                (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }

                  return (
                    <PaginationItem key={i}>
                      <PaginationLink
                        isActive={currentPage === pageNum}
                        onClick={(e) => handlePageChange(pageNum, e)}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={(e) => handlePageChange(Math.min(totalPages, currentPage + 1), e)}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
